import { useState, useCallback } from 'react';
import { FieldState, FormState } from 'formstate';
import { injectTSDI as tsdi } from '../../../../core/tsdi';
import { Api } from '../../../../core/api';
import { required, useModel } from '../../../../core/forms';
import {
    WorkoutFlagStatus,
    type ActivityFlagCreateResponseDto,
    type ActivityFlagDto
} from '../../../../core/api/FitnessActivityClient';

export function useActivityFlagStore() {
    const { fitnessActivityClient, googleUserAuth } = tsdi(Api);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const model = useModel(() => {
        const form = new FormState({
            status: new FieldState<WorkoutFlagStatus>(
                WorkoutFlagStatus.SUSPICIOUS
            ).validators(required()),
            comment: new FieldState<string>('').validators(required())
        });

        return {
            form,
            fields: {
                status: form.$.status,
                comment: form.$.comment
            }
        };
    });

    const resetForm = useCallback(() => {
        model.form.reset();
        setError(null);
    }, [model]);

    const initForm = useCallback(
        (status: WorkoutFlagStatus, comment: string) => {
            model.fields.status.onChange(status);
            model.fields.comment.onChange(comment);
            setError(null);
        },
        [model]
    );

    const createFlag = useCallback(
        async (
            activityId: number
        ): Promise<ActivityFlagCreateResponseDto | null> => {
            const validation = await model.form.validate();
            if (validation.hasError) return null;

            setSubmitting(true);
            setError(null);

            try {
                const createdBy = googleUserAuth?.email ?? 'unknown';
                const response =
                    await fitnessActivityClient.admin.activityFlagControllerCreateActivityFlag(
                        {
                            id: 0,
                            activityId,
                            status: model.fields.status.value,
                            comment: model.fields.comment.value,
                            createdBy
                        }
                    );
                return response.data;
            } catch (e: any) {
                setError(e?.message ?? 'Failed to create flag');
                return null;
            } finally {
                setSubmitting(false);
            }
        },
        [model, fitnessActivityClient, googleUserAuth]
    );

    const updateFlag = useCallback(
        async (
            flagId: number,
            activityId: number
        ): Promise<ActivityFlagDto | null> => {
            const validation = await model.form.validate();
            if (validation.hasError) return null;

            setSubmitting(true);
            setError(null);

            try {
                const modifiedBy = googleUserAuth?.email ?? 'unknown';
                const response =
                    await fitnessActivityClient.admin.activityFlagControllerUpdateActivityFlag(
                        flagId,
                        {
                            id: flagId,
                            activityId,
                            status: model.fields.status.value,
                            comment: model.fields.comment.value,
                            createdBy: '',
                            modifiedBy
                        }
                    );
                return response.data;
            } catch (e: any) {
                setError(e?.message ?? 'Failed to update flag');
                return null;
            } finally {
                setSubmitting(false);
            }
        },
        [model, fitnessActivityClient, googleUserAuth]
    );

    return {
        model,
        submitting,
        error,
        resetForm,
        initForm,
        createFlag,
        updateFlag
    };
}
